import React, { useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { APP_NAME } from '../constants/app';
import ReceiptFormatter from '../services/receiptFormatter';
import { printerService } from '../services/printer';

const OrderSlipPreview = ({ 
  orderData,
  thermalWidth = 58, // Default 58mm thermal printer width
  showPrintButton = false
}) => {

  const orderRef = useRef();
  const businessName = JSON.parse(localStorage.getItem('user'))?.entity_name || APP_NAME;

  if (!orderData) {
    return null;
  }

  // Generate order slip content using unified formatter
  const orderContent = ReceiptFormatter.formatOrderSlipForBrowserPreview(
    orderData,
    thermalWidth
  );

  const handlePrint = async () => {
    try {
      // Use the unified printer service which handles all printer types
      await printerService.printOrderSlip(orderData);
      console.log('Order slip printed successfully');
    } catch (error) {
      console.error('Error printing order slip:', error);
      
      if (error.message === 'BROWSER_PRINT_REQUESTED') {
        // Printer service is requesting browser print - use browser dialog
        console.log('Using browser print dialog as requested by printer service');
      } else {
        // Other error - show message and fall back to browser print
        console.log('Printer service failed, falling back to browser print');
      }
      
      // Always fall back to browser print for any error (including BROWSER_PRINT_REQUESTED)
      const printContent = orderRef.current;
      const printWindow = window.open('', '_blank');
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Order Slip - ${orderData.orderNumber || 'N/A'}</title>
            <style>
              @page {
                size: ${thermalWidth}mm auto;
                margin: 5mm 2mm;
              }
              body {
                font-family: 'Courier New', monospace;
                font-size: 12px;
                line-height: 1.2;
                margin: 0;
                padding: 0;
                background: white;
                color: black;
                width: ${thermalWidth}mm;
              }
              .order-content {
                width: 100%;
                white-space: pre-wrap;
                word-break: break-word;
              }
              @media print {
                body {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <div class="order-content">${printContent.innerHTML}</div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      
      // Wait for content to load, then print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  };

  return (
    <Box>
      <Paper
        ref={orderRef}
        sx={{
          p: 1,
          width: `${thermalWidth * 1.2}mm`,
          mx: 'auto',
          fontFamily: 'monospace',
          fontSize: '12px',
          lineHeight: 1.2,
          backgroundColor: 'white',
          color: 'black',
          border: '1px dashed #ccc',
          overflow: 'hidden',
        }}
      >
        <Typography
          component="pre"
          sx={{
            fontFamily: 'monospace',
            fontSize: '12px',
            lineHeight: 1.2,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: 0,
            padding: 0,
            color: 'black',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {orderContent}
        </Typography>
      </Paper>
      
      {showPrintButton && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ minWidth: 120 }}
          >
            Print Order Slip
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default OrderSlipPreview;
